// src/pages/AdminDashboard.jsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { FaEdit, FaTimes, FaTrash } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import { PACKAGES } from "../utils/entitlements";
import EmployeeAllowlistManager from "../components/EmployeeAllowlistManager";

// --- STYLED COMPONENTS ---
const DashboardWrapper = styled.div`
  padding: 2rem;
  color: var(--fontPrimaryColor);
  min-height: 100vh;
  background: var(--primaryBackgroundColor);
`;

const Title = styled.h1`
  margin-bottom: 2rem;
  color: var(--fontPrimaryColor);
`;

const UserTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;
  background: var(--secondaryBackgroundColor);
  border-radius: 12px;
  overflow: hidden;

  thead th {
    text-align: left;
    padding: 15px 20px;
    color: var(--fontSecondaryColor);
    font-weight: 600;
    font-size: 0.9em;
    text-transform: uppercase;
    background: var(--thirdBackgroundColor);
  }

  tbody tr {
    background: var(--secondaryBackgroundColor);
    width: auto;
    transition: background 0.2s;
    border-bottom: 1px solid #eee;

    &:hover {
      background: var(--thirdBackgroundColor);
    }
  }

  td {
    padding: 15px 20px;
    font-size: 0.95em;
  }
`;

const TierBadge = styled.span`
  display: inline-block;
  padding: 5px 10px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85em;
  color: white;
  background: ${(props) => {
    switch (props.tier) {
      case PACKAGES.FREE:
        return "#3498db";
      case PACKAGES.GOLD:
        return "#f39c12";
      case PACKAGES.FULL_OPTION:
        return "#2ecc71";
      default:
        return "#7f8c8d";
    }
  }};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--primaryColor);
  cursor: pointer;
  font-size: 1.1em;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

// MODAL
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--secondaryBackgroundColor);
  padding: 30px;
  border-radius: 12px;
  width: 400px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);

  h3 {
    margin-top: 0;
    color: var(--fontPrimaryColor);
  }

  label {
    font-weight: 500;
    display: block;
    margin-bottom: 5px;
  }

  input {
    width: 100%;
    padding: 10px;
    margin-bottom: 15px;
    border-radius: 6px;
    border: 1px solid #ccc;
    background: var(--thirdBackgroundColor);
    color: var(--fontPrimaryColor);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  margin-bottom: 20px;
  border-radius: 6px;
  border: 1px solid #ccc;
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
`;

const SaveButton = styled.button`
  background: var(--primaryColor);
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-right: 10px;
`;

const CancelButton = styled(SaveButton)`
  background: #7f8c8d;
`;

// Checkbox
const Checkbox = styled.input`
  margin-right: 5px;
`;

// --- COMPONENT ---
export default function AdminDashboard() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [newTier, setNewTier] = useState("");
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [extraDays, setExtraDays] = useState(0);
  const [error, setError] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const usersList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersList);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin]);

  const handleEdit = (user) => {
    setEditingUser(user);
    setNewTier(user.subscriptionTier);
    setNewName(user.name);
    setNewEmail(user.email);
    setExtraDays(0);
  };

  const handleSave = async () => {
    if (!editingUser) return;
    const userDocRef = doc(db, "users", editingUser.id);

    const updates = {};
    if (newTier !== editingUser.subscriptionTier)
      updates.subscriptionTier = newTier;
    if (newName !== editingUser.name) updates.name = newName;
    if (newEmail !== editingUser.email) updates.email = newEmail;
    if (extraDays > 0) {
      const currentEnd = editingUser.subscriptionEnd?.toDate() || new Date();
      const newEnd = new Date(currentEnd);
      newEnd.setDate(newEnd.getDate() + Number(extraDays));
      updates.subscriptionEnd = newEnd;
    }

    try {
      await updateDoc(userDocRef, updates);
      setUsers(
        users.map((u) => (u.id === editingUser.id ? { ...u, ...updates } : u))
      );
      setEditingUser(null);
    } catch (err) {
      console.error(err);
      setError("Failed to save changes.");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await deleteDoc(doc(db, "users", userId));
      setUsers(users.filter((u) => u.id !== userId));
    } catch (err) {
      console.error(err);
      setError("Failed to delete user.");
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm("Deactivate this user?")) return;
    try {
      await updateDoc(doc(db, "users", userId), { active: false });
      setUsers(
        users.map((u) => (u.id === userId ? { ...u, active: false } : u))
      );
    } catch (err) {
      console.error(err);
      setError("Failed to deactivate user.");
    }
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleBulkDeactivate = async () => {
    if (!window.confirm("Deactivate selected users?")) return;
    try {
      await Promise.all(
        selectedUsers.map((uid) =>
          updateDoc(doc(db, "users", uid), { active: false })
        )
      );
      setUsers(
        users.map((u) =>
          selectedUsers.includes(u.id) ? { ...u, active: false } : u
        )
      );
      setSelectedUsers([]);
    } catch (err) {
      console.error(err);
      setError("Failed to deactivate selected users.");
    }
  };

  if (!isAdmin) return <DashboardWrapper>Access Denied.</DashboardWrapper>;
  if (loading) return <DashboardWrapper>Loading...</DashboardWrapper>;

  return (
    <DashboardWrapper>
      <Title>Admin Dashboard</Title>
      {error && <p style={{ color: "red" }}>{error}</p>}

      <EmployeeAllowlistManager />

      <button
        onClick={handleBulkDeactivate}
        disabled={selectedUsers.length === 0}
      >
        Bulk Deactivate Selected
      </button>

      <UserTable>
        <thead>
          <tr>
            <th>
              <Checkbox disabled /> Select
            </th>
            <th>Name</th>
            <th>Email</th>
            <th>User ID</th>
            <th>Subscription Tier</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>
                <Checkbox
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => toggleSelectUser(user.id)}
                />
              </td>
              <td>{user.name}</td>
              <td>{user.email}</td>
              <td>{user.id.substring(0, 8)}...</td>
              <td>
                <TierBadge tier={user.subscriptionTier}>
                  {user.subscriptionTier}
                </TierBadge>
              </td>
              <td>{user.active === false ? "Deactivated" : "Active"}</td>
              <td style={{ gap: "1em", display: "flex" }}>
                <ActionButton onClick={() => handleEdit(user)}>
                  <FaEdit />
                </ActionButton>
                <ActionButton onClick={() => handleDeactivate(user.id)}>
                  <FaTimes />
                </ActionButton>
                <ActionButton onClick={() => handleDelete(user.id)}>
                  <FaTrash />
                </ActionButton>
              </td>
            </tr>
          ))}
        </tbody>
      </UserTable>

      {/* Edit Modal */}
      {editingUser && (
        <ModalOverlay onClick={() => setEditingUser(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Edit User: {editingUser.name}</h3>

            <label>Name</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />

            <label>Email</label>
            <input
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />

            <label>Subscription Tier</label>
            <Select
              value={newTier}
              onChange={(e) => setNewTier(e.target.value)}
            >
              {Object.values(PACKAGES).map((pkg) => (
                <option key={pkg} value={pkg}>
                  {pkg}
                </option>
              ))}
            </Select>

            <label>Extend Subscription (days)</label>
            <input
              type="number"
              min="0"
              value={extraDays}
              onChange={(e) => setExtraDays(e.target.value)}
            />

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <SaveButton onClick={handleSave}>Save</SaveButton>
              <CancelButton onClick={() => setEditingUser(null)}>
                Cancel
              </CancelButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardWrapper>
  );
}
